/**
 * HypothesisBuilderModalView — pure form UI for creating / editing a
 * hypothesis. Owns only local UI state (field values, validation, the
 * "submit attempted" flag) and delegates persistence to `onSubmit`.
 *
 * Sections:
 *  ① Title — required short label shown in breadcrumbs and lists
 *  ② "What are you trying to prove?" — required plain-language statement
 *
 * Modes (driven by `isEdit`):
 *  • Create: footer offers "Save as draft" and "Save & Run Hypothesis".
 *  • Edit: footer offers a single "Save changes" action.
 *
 * The connected `HypothesisBuilderModal` in `features/hypotheses` wires the
 * create / update / run mutations behind `onSubmit`.
 *
 * The full storybook mockup lives in
 * `storybook/src/prototypes/modern-design/hypothesis/HypothesisBuilder.stories.tsx`.
 */

import { AlertCircle, Sparkles } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import type { HypothesisItem, HypothesisItemKind } from '@/types/hypothesis'
import { MAX_HYPOTHESIS_ITEMS } from '@/types/hypothesis'
import { cn } from '@/utils/cn'

export type HypothesisBuilderAction = 'draft' | 'run' | 'edit'

/** Cleaned form payload handed to the connected wrapper on submit. */
export interface HypothesisBuilderDraft {
  title: string
  caseDescription: string
  items: HypothesisItem[]
}

export interface HypothesisBuilderInitialValues {
  title?: string
  caseDescription?: string
  items?: HypothesisItem[]
}

export interface HypothesisBuilderModalViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Edit mode: pre-fills the form and shows a single "Save changes" action. */
  isEdit: boolean
  /** Pre-filled values for edit mode. */
  initialValues?: HypothesisBuilderInitialValues
  /** True while the connected save/run is in flight. */
  submitting: boolean
  /** Persist the form. Resolve to close the dialog; reject to keep it open. */
  onSubmit: (action: HypothesisBuilderAction, draft: HypothesisBuilderDraft) => Promise<void>
}

export function HypothesisBuilderModalView({
  open,
  onOpenChange,
  isEdit,
  initialValues,
  submitting,
  onSubmit,
}: HypothesisBuilderModalViewProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] gap-4">
        <BuilderForm
          isEdit={isEdit}
          initialValues={initialValues}
          submitting={submitting}
          onClose={() => onOpenChange(false)}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  )
}

// ─── Form ────────────────────────────────────────────────────────────────────

function BuilderForm({
  isEdit,
  initialValues,
  submitting,
  onClose,
  onSubmit,
}: {
  isEdit: boolean
  initialValues?: HypothesisBuilderInitialValues
  submitting: boolean
  onClose: () => void
  onSubmit: HypothesisBuilderModalViewProps['onSubmit']
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState(initialValues?.title ?? '')
  const [description, setDescription] = useState(initialValues?.caseDescription ?? '')
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const titleValid = title.trim().length > 0
  const descriptionValid = description.trim().length > 0
  const isValid = titleValid && descriptionValid
  const titleError = submitAttempted && !titleValid
  const descriptionError = submitAttempted && !descriptionValid
  const submit = async (action: HypothesisBuilderAction) => {
    setSubmitAttempted(true)
    if (!isValid) return
    try {
      await onSubmit(action, {
        title: title.trim(),
        caseDescription: description.trim(),
        // Decomposition into sub-questions is the deep agent's job now
        // (write_todos plan) — the structured items UI was dropped
        // (DEEP_RESEARCH_REDESIGN.md decision 7).
        items: [],
      })
      onClose()
    } catch {
      // The connected wrapper surfaces the error; keep the dialog open so the
      // analyst can retry.
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isEdit ? t('search.builder.titleEdit') : t('search.builder.titleCreate')}
        </DialogTitle>
        <DialogDescription>
          {isEdit ? t('search.builder.descEdit') : t('search.builder.descCreate')}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-6">
        {/* ① Title — required */}
        <Field
          label={t('search.builder.field.title')}
          requirement="required"
          error={titleError ? t('search.builder.field.requiredError') : undefined}
        >
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={t('search.builder.placeholder.title')}
            maxLength={120}
            className={cn(
              'h-10 rounded-md border border-input bg-transparent px-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring md:h-9 md:text-sm',
              titleError && 'border-destructive'
            )}
          />
        </Field>

        {/* ② "What are you trying to prove?" — required */}
        <div className="flex flex-col gap-2">
          <h3 className="text-base font-medium tracking-tight text-foreground">
            {t('search.builder.prove')}
          </h3>
          <Field
            label={t('search.builder.field.plainLanguage')}
            requirement="required"
            error={descriptionError ? t('search.builder.field.requiredError') : undefined}
          >
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('search.builder.placeholder.description')}
              rows={3}
              className={cn('resize-none', descriptionError && 'border-destructive')}
            />
          </Field>
        </div>
      </div>

      {/* Footer — stacked full-width buttons on the phone (three of them in
          create mode do not fit one row), a right-aligned row from sm. */}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
          {t('search.builder.cancel')}
        </Button>
        {isEdit ? (
          <Button type="button" disabled={!isValid || submitting} onClick={() => submit('edit')}>
            {t('search.builder.saveChanges')}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={!isValid || submitting}
              onClick={() => submit('draft')}
            >
              {t('search.builder.saveDraft')}
            </Button>
            <Button
              type="button"
              disabled={!isValid || submitting}
              onClick={() => submit('run')}
              className="gap-1.5"
            >
              <Sparkles className="size-3.5" />
              {t('search.builder.saveAndRun')}
            </Button>
          </>
        )}
      </div>
    </>
  )
}

// ─── Field frame ─────────────────────────────────────────────────────────────

function Field({
  label,
  requirement,
  rightSlot,
  error,
  children,
}: {
  label: string
  requirement?: 'required' | 'optional'
  rightSlot?: React.ReactNode
  error?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={id}
          className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
        >
          {label}
          {requirement && (
            <>
              <span className="mx-1.5">·</span>
              <span
                className={cn(
                  requirement === 'required'
                    ? 'font-semibold text-foreground'
                    : 'text-muted-foreground/70'
                )}
              >
                {t(`search.builder.field.${requirement}`)}
              </span>
            </>
          )}
        </label>
        {rightSlot}
      </div>
      {children}
      {error && (
        <div className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="size-3" />
          {error}
        </div>
      )}
    </div>
  )
}

// ─── Item row ────────────────────────────────────────────────────────────────

// ─── Kind toggle ─────────────────────────────────────────────────────────────

function KindToggle({
  value,
  onChange,
}: {
  value: HypothesisItemKind
  onChange: (v: HypothesisItemKind) => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={() => onChange(value === 'question' ? 'claim' : 'question')}
      title={t('search.builder.toggleKind')}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-input bg-background px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground hover:bg-muted/40"
    >
      <span>{t(`search.builder.itemKind.${value}`)}</span>
      <span className="text-[8px] text-muted-foreground/70">▾</span>
    </button>
  )
}
